"use client"

import { useEffect, useState } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import {
  ALL_GRANTED,
  DEFAULT_DENIED,
  readStoredConsent,
  writeStoredConsent,
  type ConsentCategories,
} from "@lib/util/cookie-consent"

type View = "banner" | "settings" | "hidden"

export default function CookieConsent() {
  const [view, setView] = useState<View>("hidden")
  const [draft, setDraft] = useState<ConsentCategories>(DEFAULT_DENIED)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const stored = readStoredConsent()
    if (stored) {
      setDraft(stored.categories)
      setView("hidden")
    } else {
      setDraft(DEFAULT_DENIED)
      setView("banner")
    }
    setHydrated(true)

    const openSettings = () => {
      const current = readStoredConsent()?.categories ?? DEFAULT_DENIED
      setDraft(current)
      setView("settings")
    }

    window.addEventListener("vx:open-cookie-settings", openSettings)
    return () =>
      window.removeEventListener("vx:open-cookie-settings", openSettings)
  }, [])

  useEffect(() => {
    if (!hydrated) {
      return
    }
    if (view === "banner" || view === "settings") {
      document.body.classList.add("cookie-banner-open")
    } else {
      document.body.classList.remove("cookie-banner-open")
    }
    return () => document.body.classList.remove("cookie-banner-open")
  }, [view, hydrated])

  const save = (categories: ConsentCategories) => {
    writeStoredConsent(categories)
    setDraft(categories)
    setView("hidden")
    window.dispatchEvent(new CustomEvent("vx:cookie-consent-changed"))
  }

  if (!hydrated || view === "hidden") {
    return null
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] p-3 xsmall:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent"
    >
      <div className="mx-auto max-w-3xl border border-vx-line bg-vx-surface shadow-[0_-8px_32px_rgba(11,15,22,0.12)]">
        {view === "banner" ? (
          <div className="p-4 xsmall:p-5">
            <p
              id="cookie-consent-title"
              className="font-mono text-[10px] uppercase tracking-[0.16em] text-vx-muted"
            >
              Cookie &amp; Privacy · PDPA
            </p>
            <h2 className="mt-2 text-base font-semibold text-vx-ink xsmall:text-lg">
              การใช้คุกกี้และข้อมูลส่วนบุคคล
            </h2>
            <p className="mt-2 text-sm leading-6 text-vx-muted">
              เว็บไซต์นี้ใช้คุกกี้ที่จำเป็นต่อการทำงานของร้านค้า
              และขอความยินยอมก่อนใช้คุกกี้เพื่อวิเคราะห์การใช้งานและโฆษณา
              ตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA)
              และข้อกำหนด Google Consent Mode
            </p>
            <p className="mt-2 text-sm leading-6 text-vx-muted">
              คุณสามารถ{" "}
              <button
                type="button"
                className="text-vx-accent underline-offset-2 hover:underline"
                onClick={() => setView("settings")}
              >
                เลือกประเภทคุกกี้
              </button>{" "}
              หรืออ่านรายละเอียดใน{" "}
              <LocalizedClientLink
                href="/privacy"
                className="text-vx-accent underline-offset-2 hover:underline"
              >
                นโยบายความเป็นส่วนตัว
              </LocalizedClientLink>
            </p>
            <div className="mt-4 flex flex-col gap-2 xsmall:flex-row xsmall:flex-wrap">
              <button
                type="button"
                onClick={() => save(ALL_GRANTED)}
                className="bg-vx-accent px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0056cc]"
                data-testid="cookie-accept-all"
              >
                ยอมรับทั้งหมด
              </button>
              <button
                type="button"
                onClick={() => save(DEFAULT_DENIED)}
                className="border border-vx-line bg-vx-bg px-4 py-2.5 text-sm font-medium text-vx-ink transition-colors hover:border-vx-accent/40"
                data-testid="cookie-reject"
              >
                ใช้เฉพาะที่จำเป็น
              </button>
              <button
                type="button"
                onClick={() => setView("settings")}
                className="px-4 py-2.5 text-sm font-medium text-vx-muted transition-colors hover:text-vx-ink"
                data-testid="cookie-customize"
              >
                ตั้งค่า
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 xsmall:p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-vx-muted">
                  Cookie settings
                </p>
                <h2
                  id="cookie-consent-title"
                  className="mt-2 text-base font-semibold text-vx-ink"
                >
                  ตั้งค่าคุกกี้
                </h2>
              </div>
              <button
                type="button"
                onClick={() =>
                  setView(readStoredConsent() ? "hidden" : "banner")
                }
                className="text-sm text-vx-muted hover:text-vx-ink"
                aria-label="ปิด"
              >
                ปิด
              </button>
            </div>

            <ul className="mt-4 space-y-3">
              <CategoryRow
                title="คุกกี้ที่จำเป็น"
                description="จำเป็นต่อการทำงานของเว็บไซต์ เช่น ตะกร้าสินค้า ภูมิภาค และความปลอดภัย — ไม่สามารถปิดได้"
                checked
                disabled
              />
              <CategoryRow
                title="คุกกี้วิเคราะห์ (Analytics)"
                description="ช่วยวัดสถิติการใช้งานเว็บไซต์ เช่น Google Analytics เพื่อปรับปรุงประสบการณ์"
                checked={draft.analytics}
                onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
              />
              <CategoryRow
                title="คุกกี้การตลาด (Marketing)"
                description="ใช้สำหรับโฆษณาและการวัดผลแคมเปญ เช่น Google Ads / remarketing"
                checked={draft.marketing}
                onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
              />
              <CategoryRow
                title="คุกกี้การตั้งค่า (Preferences)"
                description="จดจำการตั้งค่าที่ไม่จำเป็น เช่น ภาษาหรือตัวเลือกการแสดงผลเพิ่มเติม"
                checked={draft.preferences}
                onChange={(v) => setDraft((d) => ({ ...d, preferences: v }))}
              />
            </ul>

            <p className="mt-4 text-xs leading-5 text-vx-muted">
              คุณสามารถถอนความยินยอมได้ตลอดเวลาผ่านลิงก์ “ตั้งค่าคุกกี้”
              ท้ายหน้าเว็บ ตามสิทธิ์ภายใต้ PDPA
            </p>

            <div className="mt-4 flex flex-col gap-2 xsmall:flex-row">
              <button
                type="button"
                onClick={() => save({ ...draft, necessary: true })}
                className="bg-vx-accent px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0056cc]"
                data-testid="cookie-save-settings"
              >
                บันทึกการตั้งค่า
              </button>
              <button
                type="button"
                onClick={() => save(ALL_GRANTED)}
                className="border border-vx-line bg-vx-bg px-4 py-2.5 text-sm font-medium text-vx-ink transition-colors hover:border-vx-accent/40"
              >
                ยอมรับทั้งหมด
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange?: (value: boolean) => void
}) {
  return (
    <li className="flex gap-3 border border-vx-line bg-vx-bg p-3">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-vx-accent"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        aria-label={title}
      />
      <div>
        <p className="text-sm font-semibold text-vx-ink">{title}</p>
        <p className="mt-1 text-xs leading-5 text-vx-muted">{description}</p>
      </div>
    </li>
  )
}
